import React, { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Group, Loader, NumberInput,
  Paper, Select, SimpleGrid, Stack, Text, Textarea, Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import HttpClient from '../helpers/HttpClient';
import { Contract } from '../types/contract.type';
import { Person } from '../types/person.type';
import { ContractType } from '../types/ContractType';
import { PaymentAccount } from '../types/payment-account.type';

const TYPE_OPTIONS = [
  { value: ContractType.PLAYER, label: 'Spieler' },
  { value: ContractType.COACH,  label: 'Trainer' },
  { value: ContractType.STAFF,  label: 'Staff' },
  { value: ContractType.OTHER,  label: 'Sonstige' },
];

const MONTHS: [keyof Contract, string][] = [
  ['fixedMonthJan', 'Jan'], ['fixedMonthFeb', 'Feb'], ['fixedMonthMar', 'Mär'],
  ['fixedMonthApr', 'Apr'], ['fixedMonthMay', 'Mai'], ['fixedMonthJun', 'Jun'],
  ['fixedMonthJul', 'Jul'], ['fixedMonthAug', 'Aug'], ['fixedMonthSep', 'Sep'],
  ['fixedMonthOct', 'Okt'], ['fixedMonthNov', 'Nov'], ['fixedMonthDec', 'Dez'],
];

const contractClient = new HttpClient<Contract>('/contract');

const ContractEdit: React.FC<{ id?: number }> = ({ id }) => {
  const navigate = useNavigate();
  const isNew = !id;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [persons, setPersons] = useState<Person[]>([]);
  const [paymentAccounts, setPaymentAccounts] = useState<PaymentAccount[]>([]);
  const [paymentAccountId, setPaymentAccountId] = useState<string | null>(null);

  const [contractType, setContractType] = useState<ContractType>(ContractType.PLAYER);
  const [personId, setPersonId]         = useState<string | null>(null);
  const [team, setTeam]                 = useState<string | null>('KM');
  const [teamOptions, setTeamOptions]   = useState<string[]>([]);
  const [validFrom, setValidFrom]       = useState<string | null>(null);
  const [validTo, setValidTo]           = useState<string | null>(null);

  const [forTest, setForTest]           = useState(false);
  const [forCup, setForCup]             = useState(false);
  const [forLeague, setForLeague]       = useState(true);

  const [fixedAmount, setFixedAmount]           = useState<number | string>(0);
  const [startingAmount, setStartingAmount]     = useState<number | string>(0);
  const [substitutionAmount, setSubstitutionAmount] = useState<number | string>(0);
  const [perPointAmount, setPerPointAmount]     = useState<number | string>(0);
  const [perSquadAmount, setPerSquadAmount]     = useState<number | string>(0);
  const [trainingLimit, setTrainingLimit]       = useState<number | string>('');

  const [notes, setNotes] = useState<string>('');

  const [months, setMonths] = useState<Record<string, boolean>>({
    fixedMonthJan: true, fixedMonthFeb: true, fixedMonthMar: true,
    fixedMonthApr: true, fixedMonthMay: true, fixedMonthJun: true,
    fixedMonthJul: true, fixedMonthAug: true, fixedMonthSep: true,
    fixedMonthOct: true, fixedMonthNov: true, fixedMonthDec: true,
  });

  const params = new URLSearchParams(window.location.search);
  const personIdFromQuery = params.get('personId');

  useEffect(() => {
    const personClient = new HttpClient<Person>('/person');
    const accountClient = new HttpClient<PaymentAccount>('/payment-account');
    const filterOptionsClient = new HttpClient<{ teams: string[] }>('/match/filter-options');
    Promise.all([
      personClient.getAll({ slim: 'true' }),
      accountClient.getAll(),
      filterOptionsClient.getSingle().catch(() => null),
      id ? contractClient.getById(id) : Promise.resolve(null),
    ]).then(([ps, accounts, filterOptions, contractRes]) => {
      setPersons(ps as Person[]);
      setPaymentAccounts(accounts as PaymentAccount[]);
      setTeamOptions(filterOptions?.teams ?? []);
      if (contractRes) {
        const c = (contractRes as any).data as Contract;
        setContractType(c.contractType ?? ContractType.PLAYER);
        setPersonId(c.person?.id?.toString() ?? null);
        setTeam(c.team ?? null);
        setValidFrom(c.validFrom ? new Date(c.validFrom).toISOString().slice(0, 10) : null);
        setValidTo(c.validTo ? new Date(c.validTo).toISOString().slice(0, 10) : null);
        setForTest(c.forTestMatches ?? false);
        setForCup(c.forCupMatches ?? false);
        setForLeague(c.forLeagueMatches ?? true);
        setFixedAmount(c.fixedPerMonthAmount ?? 0);
        setStartingAmount(c.startingSquadAmount ?? 0);
        setSubstitutionAmount(c.substitionInAmount ?? 0);
        setPerPointAmount(c.perPointAmount ?? 0);
        setPerSquadAmount(c.perSquadAmount ?? 0);
        setTrainingLimit(c.trainingAttendanceLimit ?? '');
        setMonths({
          fixedMonthJan: c.fixedMonthJan ?? true, fixedMonthFeb: c.fixedMonthFeb ?? true,
          fixedMonthMar: c.fixedMonthMar ?? true, fixedMonthApr: c.fixedMonthApr ?? true,
          fixedMonthMay: c.fixedMonthMay ?? true, fixedMonthJun: c.fixedMonthJun ?? true,
          fixedMonthJul: c.fixedMonthJul ?? true, fixedMonthAug: c.fixedMonthAug ?? true,
          fixedMonthSep: c.fixedMonthSep ?? true, fixedMonthOct: c.fixedMonthOct ?? true,
          fixedMonthNov: c.fixedMonthNov ?? true, fixedMonthDec: c.fixedMonthDec ?? true,
        });
        setNotes(c.notes ?? '');
        setPaymentAccountId(c.paymentAccount?.id?.toString() ?? null);
      } else if (personIdFromQuery) {
        setPersonId(personIdFromQuery);
      }
    }).finally(() => setLoading(false));
  }, [id]);

  const handleSave = async () => {
    if (!personId || !validFrom) {
      notifications.show({ color: 'red', message: 'Bitte Person und Gültig-ab-Datum ausfüllen.' });
      return;
    }
    setSaving(true);
    try {
      const payload: any = {
        id: id ?? 0,
        contractType,
        person: { id: Number(personId) },
        team: team || undefined,
        validFrom,
        validTo: validTo || undefined,
        forTestMatches: forTest,
        forCupMatches: forCup,
        forLeagueMatches: forLeague,
        fixedPerMonthAmount: Number(fixedAmount),
        startingSquadAmount: Number(startingAmount),
        substitionInAmount: Number(substitutionAmount),
        perPointAmount: Number(perPointAmount),
        perSquadAmount: Number(perSquadAmount),
        // Empty or 0 = no training attendance deduction.
        trainingAttendanceLimit: Number(trainingLimit) > 0 ? Number(trainingLimit) : null,
        ...months,
        paymentAccount: paymentAccountId ? { id: Number(paymentAccountId) } : undefined,
        notes: notes || undefined,
      };
      const res: any = await contractClient.post(payload);
      notifications.show({ color: 'green', message: 'Gespeichert.' });
      if (isNew) {
        navigate(`/contract/edit/${res?.data?.id ?? ''}`);
      }
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Speichern.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !confirm('Vertrag löschen?')) return;
    try {
      const res = await contractClient.delete(id);
      if (res.status !== 200) throw new Error();
      navigate('/contract');
    } catch {
      notifications.show({ color: 'red', message: 'Fehler beim Löschen.' });
    }
  };

  if (loading) return <Loader mt="xl" />;

  const personOptions = persons.map((p) => ({ value: p.id.toString(), label: `${p.firstName} ${p.lastName}` }));
  const accountOptions = paymentAccounts.map((a) => ({ value: a.id.toString(), label: a.name }));

  return (
    <Stack p="md" gap="md" pb={120}>
      <Group justify="space-between" align="center" wrap="nowrap">
        <Title order={2}>{isNew ? 'Neuer Vertrag' : 'Vertrag'}</Title>
      </Group>

      {/* Stammdaten */}
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Stack gap="sm">
          <Select
            label="Typ"
            data={TYPE_OPTIONS}
            value={contractType}
            onChange={(v) => setContractType((v as ContractType) ?? ContractType.PLAYER)}
            required
          />
          <Select
            label="Person"
            data={personOptions}
            value={personId}
            onChange={setPersonId}
            searchable
            clearable
            required
          />
          <Select
            label="Zahlungskonto"
            data={accountOptions}
            value={paymentAccountId}
            onChange={setPaymentAccountId}
            searchable
            clearable
            placeholder="Standard (Umgebungsvariable)"
          />
          <Select
            label="Team"
            data={teamOptions}
            value={team}
            onChange={setTeam}
            searchable
            clearable
            placeholder="Alle Teams"
            description="Nur Spiele dieses Teams zählen für die Spielprämien. Leer = alle Teams."
          />
          <Group grow wrap="wrap" gap="sm">
            <DateInput
              label="Gültig ab"
              value={validFrom}
              onChange={setValidFrom}
              valueFormat="DD.MM.YYYY"
              required
              clearable
            />
            <DateInput
              label="Gültig bis"
              value={validTo}
              onChange={setValidTo}
              valueFormat="DD.MM.YYYY"
              clearable
            />
          </Group>
        </Stack>
      </Paper>

      {/* Spieltypen */}
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Text fw={600} size="sm" mb="sm">Anwendbare Spieltypen</Text>
        <Group gap="xl">
          <Checkbox label="Testspiele" checked={forTest}   onChange={(e) => setForTest(e.currentTarget.checked)} />
          <Checkbox label="Cup"        checked={forCup}    onChange={(e) => setForCup(e.currentTarget.checked)} />
          <Checkbox label="Liga"       checked={forLeague} onChange={(e) => setForLeague(e.currentTarget.checked)} />
        </Group>
      </Paper>

      {/* Fixbetrag */}
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Text fw={600} size="sm" mb="sm">Monatliches Fixum</Text>
        <NumberInput
          label="Betrag (€)"
          value={fixedAmount}
          onChange={setFixedAmount}
          min={0}
          prefix="€ "
          mb="sm"
        />
        <Text size="xs" c="dimmed" mb={6}>Auszahlung in folgenden Monaten:</Text>
        <SimpleGrid cols={6} spacing="xs">
          {MONTHS.map(([field, label]) => (
            <Checkbox
              key={field}
              label={label}
              checked={months[field as string] ?? true}
              onChange={(e) => { const checked = e.currentTarget.checked; setMonths((prev) => ({ ...prev, [field]: checked })); }}
            />
          ))}
        </SimpleGrid>
      </Paper>

      {/* Spielprämien */}
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Text fw={600} size="sm" mb="sm">Spielprämien</Text>
        <Stack gap="sm">
          <NumberInput label="Startelf-Prämie (€)"     value={startingAmount}     onChange={setStartingAmount}     min={0} prefix="€ " />
          <NumberInput label="Einwechslung-Prämie (€)" value={substitutionAmount} onChange={setSubstitutionAmount} min={0} prefix="€ " />
          <NumberInput label="Punkt-Prämie (€)"        value={perPointAmount}     onChange={setPerPointAmount}     min={0} prefix="€ " />
          <NumberInput label="Kader-Prämie (€)"        value={perSquadAmount}     onChange={setPerSquadAmount}     min={0} prefix="€ " />
        </Stack>
      </Paper>

      {/* Trainingsbeteiligung */}
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Text fw={600} size="sm" mb="sm">Trainingsbeteiligung</Text>
        <NumberInput
          label="Mindest-Trainingsbeteiligung (%)"
          description="Liegt die Beteiligung im Abrechnungszeitraum darunter, wird die Differenz in % von der Abrechnung abgezogen (z.B. Limit 80, Beteiligung 70 → 10 % Abzug). Entschuldigte Abwesenheiten zählen nicht. Leer oder 0 = kein Abzug."
          value={trainingLimit}
          onChange={setTrainingLimit}
          min={0}
          max={100}
          suffix=" %"
          allowDecimal={false}
        />
      </Paper>

      {/* Notizen */}
      <Paper shadow="sm" p="md" radius="md" withBorder>
        <Text fw={600} size="sm" mb="sm">Notizen / Sondervereinbarungen</Text>
        <Textarea
          placeholder="Besondere Vereinbarungen, Zusatzklauseln..."
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          autosize
          minRows={3}
        />
      </Paper>

      {/* Sticky footer */}
      <Box
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--mantine-color-body)',
          padding: '12px 0',
          borderTop: '1px solid var(--mantine-color-default-border)',
          zIndex: 100,
        }}
      >
        <Group gap="sm" wrap="wrap">
          <Button onClick={handleSave} loading={saving} size="md" leftSection={<IconPlus size={16} />}>
            Speichern
          </Button>
          {!isNew && (
            <Button color="red" variant="light" onClick={handleDelete} size="md">
              Löschen
            </Button>
          )}
          <Button variant="subtle" onClick={() => navigate('/contract')} size="md">
            Zurück
          </Button>
        </Group>
      </Box>
    </Stack>
  );
};

export default ContractEdit;
